import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, Plus, Upload, Users, HandCoins } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  PURPOSE_LABEL,
  type CampaignPurpose,
  type ContactabilityPolicyRecord,
  type ContactAgent,
  type ContactAgentDashboardMetrics,
} from '@workspace/types';
import { CAMPAIGNS_HUB_PATH } from '../api/campaignPaths';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { ContactAgentMetricCard } from '../components/ContactAgentMetricCard';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';

export function ContactAgentDashboardPage() {
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [agents, setAgents] = useState<ContactAgent[]>([]);
  const [policies, setPolicies] = useState<ContactabilityPolicyRecord[]>([]);
  const [metrics, setMetrics] = useState<ContactAgentDashboardMetrics | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void Promise.all([
      contactAgentApi.listAgents(),
      contactAgentApi.listPolicies().catch(() => [] as ContactabilityPolicyRecord[]),
      contactAgentApi.getDashboard().catch(() => null),
    ])
      .then(([agentList, policyList, board]) => {
        if (cancelled) {
          return;
        }
        setAgents(agentList);
        setPolicies(policyList);
        setMetrics(board);
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId]);

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Agente de contacto'
        description='Agentes, cartera, excepciones y políticas. Las campañas viven en el hub Campañas.'
        showBack={false}
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
        actions={
          <>
            <Button variant='outline' asChild>
              <Link to='/admin/contact-agent/import'>
                <Upload className='mr-2 h-4 w-4' />
                Importar cartera
              </Link>
            </Button>
            <Button variant='outline' asChild>
              <Link to='/admin/contact-agent/contacts'>
                <Users className='mr-2 h-4 w-4' />
                Contactos
              </Link>
            </Button>
            <Button variant='outline' asChild>
              <Link to='/admin/contact-agent/inbox'>
                <Inbox className='mr-2 h-4 w-4' />
                Bandeja
              </Link>
            </Button>
            <Button variant='outline' asChild>
              <Link to='/admin/contact-agent/payment-promises'>
                <HandCoins className='mr-2 h-4 w-4' />
                Compromisos de pago
              </Link>
            </Button>
            <Button variant='outline' asChild>
              <Link to={CAMPAIGNS_HUB_PATH}>Ver en Campañas</Link>
            </Button>
            <Button asChild>
              <Link to='/admin/contact-agent/agents/new'>
                <Plus className='mr-2 h-4 w-4' />
                Nuevo agente
              </Link>
            </Button>
          </>
        }
      />

      {error ? <p className='text-destructive'>{error}</p> : null}

      {metrics ? (
        <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
          <ContactAgentMetricCard
            title='Campañas activas'
            value={String(metrics.started + metrics.resumed)}
            hint={`${metrics.paused} pausadas · ${metrics.draft} borrador`}
          />
          <ContactAgentMetricCard
            title='Conversaciones iniciadas'
            value={String(metrics.conversationsStarted)}
          />
          <ContactAgentMetricCard
            title='Respondieron'
            value={String(metrics.conversationsResponded)}
          />
          <ContactAgentMetricCard
            title='Sin respuesta'
            value={String(metrics.conversationsNoResponse)}
          />
        </div>
      ) : null}

      <ContactabilityCard policies={policies} onSaved={setPolicies} onError={setError} />

      <Card className='rounded-xl shadow-sm'>
        <CardHeader className='flex flex-row items-center justify-between'>
          <CardTitle className='text-lg'>Agentes</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          {agents.length === 0 ? (
            <div className='rounded-xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground'>
              Todavía no hay un agente. Crealo y asignale un bot de cobranzas.
            </div>
          ) : (
            agents.map(agent => (
              <div key={agent.id} className='flex items-center justify-between rounded-xl border px-4 py-3'>
                <div>
                  <p className='font-medium'>{agent.name}</p>
                  <p className='text-sm text-muted-foreground'>{agent.bot?.name ?? 'Cobranzas'}</p>
                </div>
                <div className='flex items-center gap-2'>
                  <Badge variant='secondary'>{agent.domain}</Badge>
                  <Button size='sm' variant='outline' asChild>
                    <Link to={`/admin/contact-agent/agents/${agent.id}`}>Editar</Link>
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </ContactAgentPage>
  );
}

function ContactabilityCard({
  policies,
  onSaved,
  onError,
}: {
  policies: ContactabilityPolicyRecord[];
  onSaved: (policies: ContactabilityPolicyRecord[]) => void;
  onError: (message: string) => void;
}) {
  const save = async (purpose: CampaignPurpose, legalBasis: string) => {
    const basis = legalBasis.slice(0, 128).trim();
    if (basis.length < 3) {
      onError('Indicá el fundamento legal. No se infiere automáticamente.');
      return;
    }
    try {
      await contactAgentApi.upsertPolicy({
        purpose,
        legalBasis: basis,
        requiresConsent: false,
      });
      onSaved(await contactAgentApi.listPolicies());
    } catch (err) {
      onError(contactAgentError(err));
    }
  };
  return (
    <Card className='rounded-xl shadow-sm'>
      <CardHeader>
        <CardTitle className='text-lg'>Política de contactabilidad</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3 text-sm'>
        <p className='text-muted-foreground'>
          Sin política vigente no se pueden iniciar campañas. El fundamento lo escribe un administrador.
        </p>
        {(['COBRANZAS', 'AVISO'] as CampaignPurpose[]).map(purpose => {
          const current = policies.find(policy => policy.purpose === purpose && !policy.revokedAt);
          return (
            <PolicyRow
              key={`${purpose}-${current?.id ?? 'new'}`}
              purpose={purpose}
              current={current}
              onSave={legalBasis => void save(purpose, legalBasis)}
            />
          );
        })}
      </CardContent>
    </Card>
  );
}

function PolicyRow({
  purpose,
  current,
  onSave,
}: {
  purpose: CampaignPurpose;
  current?: ContactabilityPolicyRecord;
  onSave: (legalBasis: string) => void;
}) {
  const [basis, setBasis] = useState(current?.legalBasis ?? '');
  return (
    <div className='flex flex-wrap items-end gap-2'>
      <div className='space-y-1'>
        <Label htmlFor={`policy-${purpose}`}>{PURPOSE_LABEL[purpose]}</Label>
        <Input
          id={`policy-${purpose}`}
          value={basis}
          maxLength={128}
          placeholder='Fundamento (lo define el administrador)'
          onChange={event => setBasis(event.target.value)}
        />
      </div>
      <Button size='sm' variant='outline' type='button' onClick={() => onSave(basis)}>
        {current ? `Actualizar v${current.policyVersion}` : 'Crear política'}
      </Button>
    </div>
  );
}
